import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ScheduleBody, Visit } from '../domain/visit';
import { visitsApi, type ListVisitsParams } from '../infrastructure/api';

export const PAGE_SIZE = 20;

export const visitKeys = {
  all: ['visits'] as const,
  lists: () => [...visitKeys.all, 'list'] as const,
  list: (params: ListVisitsParams) => [...visitKeys.lists(), params] as const,
  one: (id: string) => [...visitKeys.all, 'one', id] as const,
};

export const visitsQuery = (params: ListVisitsParams) =>
  queryOptions({
    queryKey: visitKeys.list(params),
    queryFn: () => visitsApi.list(params),
    // Pedido novo chega do portal a qualquer momento: a lista se atualiza sozinha.
    refetchInterval: 60_000,
  });

export const visitQuery = (id: string) =>
  queryOptions({ queryKey: visitKeys.one(id), queryFn: () => visitsApi.get(id) });

export const useVisits = (params: ListVisitsParams) =>
  useQuery({ ...visitsQuery(params), placeholderData: keepPreviousData });

export const useVisit = (id: string | undefined) => useQuery({ ...visitQuery(id ?? ''), enabled: !!id });

/** Toda ação devolve o pedido inteiro: grava no cache e recarrega as listas (contagens mudam). */
function useVisitAction<V>(fn: (vars: V) => Promise<Visit>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (visit) => {
      qc.setQueryData(visitKeys.one(visit.id), visit);
      void qc.invalidateQueries({ queryKey: visitKeys.lists() });
    },
    // Outra pessoa do time mexeu antes: o estado certo vem da API.
    onError: () => void qc.invalidateQueries({ queryKey: visitKeys.all }),
  });
}

export const useMarkContacting = () => useVisitAction((id: string) => visitsApi.contacting(id));
export const useScheduleVisit = () =>
  useVisitAction(({ id, body }: { id: string; body: ScheduleBody }) => visitsApi.schedule(id, body));
export const useCompleteVisit = () => useVisitAction((id: string) => visitsApi.complete(id));
export const useCancelVisit = () =>
  useVisitAction(({ id, reason }: { id: string; reason: string }) => visitsApi.cancel(id, reason));
